import { useEffect, useRef, useState } from 'react';
import type { AdminMediaView } from '@quiz/shared';

/** 75 phones is the whole room. The number that matters is never the file size. */
const ROOM = 75;

function mb(bytes: number): string {
  return bytes < 1_000_000 ? `${Math.round(bytes / 1000)} KB` : `${(bytes / 1_000_000).toFixed(2)} MB`;
}

function seconds(ms: number | null): string {
  return ms === null ? '' : `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Attach a picture, clip or video to a question.
 *
 * Shows what the room pays, not what the file weighs. An author choosing a clip
 * needs to know it costs the venue 167MB across 75 handsets, and that number is
 * invisible from a file browser -- which is how a picture round ends up heavier
 * than a video round without anyone deciding it should be.
 */
export function MediaField({
  token,
  value,
  onChange,
}: {
  token: string;
  value: AdminMediaView | null;
  onChange: (m: AdminMediaView | null) => void;
}) {
  const [kind, setKind] = useState<'IMAGE' | 'AUDIO' | 'VIDEO'>('IMAGE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      // Not through api(): that sets a JSON content-type and, more importantly,
      // an 8-second deadline. A video transcode is a couple of seconds on the
      // NAS but the upload in front of it is not instant.
      const res = await fetch('/api/admin/media', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body: form,
      });
      /**
       * Not res.json() straight off. A rejection from the reverse proxy never
       * reaches the app and is an HTML error page, so parsing it first turns
       * "your file is too big" into "Unexpected token '<'" -- which is exactly
       * what a 20MB video produced while an 840KB picture worked, and says
       * nothing about the cause.
       */
      type UploadBody = AdminMediaView & { deduped?: boolean; error?: { message: string } };
      const text = await res.text();
      let body: UploadBody | null = null;
      try {
        body = JSON.parse(text) as UploadBody;
      } catch {
        body = null;
      }

      if (!res.ok || !body) {
        if (res.status === 413) {
          throw new Error(
            'That file was rejected before it reached the server — it is over the upload limit. ' +
              'Shorten or re-encode it, or raise client_max_body_size on the proxy.',
          );
        }
        throw new Error(
          body?.error?.message ??
            `That upload failed (${res.status}). The server did not send a readable answer.`,
        );
      }
      onChange(body);
      setNote(
        body.deduped
          ? 'Already in the library — reusing the stored copy, so the room downloads it once.'
          : `Encoded to ${mb(body.bytes)}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That upload failed.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  if (value) {
    return (
      <div className="rounded-xl border border-ink-3 bg-ink-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">
              {value.kind[0] + value.kind.slice(1).toLowerCase()}
              {value.width ? ` · ${value.width}×${value.height}` : ''}
              {value.durationMs ? ` · ${seconds(value.durationMs)}` : ''}
            </p>
            <p className="mt-0.5 text-xs text-muted">
              {mb(value.bytes)} each · <b>{mb(value.bytes * ROOM)}</b> across {ROOM} phones
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setNote(null);
            }}
            className="shrink-0 rounded-lg bg-ink-2 px-3 py-1.5 text-sm hover:bg-ink-3"
          >
            Remove
          </button>
        </div>
        <MediaPreview token={token} media={value} />
        {note && <p className="mt-2 text-xs text-go">{note}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dashed border-ink-3 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-lg border border-ink-3">
          {(['IMAGE', 'AUDIO', 'VIDEO'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`px-3 py-1.5 text-xs font-semibold ${
                kind === k ? 'bg-go text-ink' : 'text-muted hover:text-chalk'
              }`}
            >
              {k[0] + k.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        <input
          ref={input}
          type="file"
          disabled={busy}
          accept={kind === 'IMAGE' ? 'image/*' : kind === 'AUDIO' ? 'audio/*' : 'video/*'}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
          className="text-xs text-muted file:mr-2 file:rounded-lg file:border-0 file:bg-ink-2 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-chalk"
        />
      </div>
      <p className="mt-2 text-xs text-muted">
        {busy
          ? 'Encoding…'
          : kind === 'VIDEO'
            ? 'Re-encoded to 480p, 20s maximum. Every second has to reach 75 phones before the round starts.'
            : kind === 'AUDIO'
              ? 'Re-encoded to Opus, 45s maximum.'
              : 'Re-encoded to WebP, 1080px maximum.'}
      </p>
      {error && <p className="mt-2 text-xs text-flag">{error}</p>}
    </div>
  );
}

/**
 * Play back what the room will actually get.
 *
 * The ENCODED file, never the upload. A 4K master and its 480p transcode look
 * identical in a file browser and are not the same thing at all -- the point of
 * previewing is to catch the crop that lost the face, or the clip trimmed a
 * second short, before the question reaches a room rather than after.
 *
 * Fetched rather than linked because the admin token is a header, so an
 * <img src> could not carry it.
 */
function MediaPreview({ token, media }: { token: string; media: AdminMediaView }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    setFailed(false);
    void fetch(`/api/admin/media/${media.id}/file`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => {
        if (cancelled) return;
        revoke = URL.createObjectURL(b);
        setUrl(revoke);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      // Released on the way out. A builder session that edits thirty questions
      // would otherwise hold thirty decoded videos in memory.
      if (revoke) URL.revokeObjectURL(revoke);
      setUrl(null);
    };
  }, [media.id, token]);

  if (failed) {
    return (
      <p className="mt-2 text-xs text-flag">
        The file could not be loaded. It may be missing from the media store &mdash; check that the
        media volume was restored alongside the database.
      </p>
    );
  }

  if (!url) return <p className="mt-2 text-xs text-muted">Loading preview&hellip;</p>;

  if (media.kind === 'IMAGE') {
    return (
      <img
        src={url}
        alt=""
        className="mt-2 max-h-56 w-full rounded-lg bg-ink object-contain"
      />
    );
  }

  if (media.kind === 'AUDIO') {
    return <audio src={url} controls className="mt-2 w-full" />;
  }

  return (
    <video
      src={url}
      controls
      playsInline
      // Matches how a player will see it: inline, no fullscreen. Previewing in
      // a mode the room will never get would defeat the point.
      disablePictureInPicture
      controlsList="nofullscreen nodownload noremoteplayback"
      className="mt-2 max-h-56 w-full rounded-lg bg-ink"
    />
  );
}
